import type { CampaignChartPoint, CampaignMetricTotals, ChartMetric } from '@metapic-apps/shared'
import { type Ref, ref, watch } from 'vue'

import {
  normalizeCampaignChart,
  normalizeCampaignMetricTotals,
} from '@/lib/adapters/campaign-performance.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'

export const SOCIAL_MEDIA_CAMPAIGN_METRICS = [
  'views',
  'unique_views',
  'media_value',
  'content_produced',
] as const

type DateRange = { startDate: string; endDate: string }

export const useSocialMediaCampaignMetricTotals = (
  advertiserId: number,
  campaignId: number,
  dateRange: Ref<DateRange>,
) => {
  const data = ref<CampaignMetricTotals>({})
  const { loading, fetchTotals } = useApi({
    fetchTotals: async ({ startDate, endDate }: DateRange) => {
      const response = await socialMediaClient.summary({
        advertiser_id: advertiserId,
        campaign_id: campaignId,
        date_from: startDate,
        date_to: endDate,
        traffic_source: 'all',
        metrics: [...SOCIAL_MEDIA_CAMPAIGN_METRICS],
      })
      data.value = normalizeCampaignMetricTotals(response.metrics)
    },
  })

  watch(dateRange, () => void fetchTotals(dateRange.value), { immediate: true })

  return { totals: data, isLoading: loading }
}

export const useSocialMediaCampaignChart = (
  advertiserId: number,
  campaignId: number,
  metric: Ref<ChartMetric | null>,
  dateRange: Ref<DateRange>,
) => {
  const data = ref<CampaignChartPoint[]>([])
  const { loading, fetchChart } = useApi({
    fetchChart: async () => {
      const activeMetric = metric.value
      if (!activeMetric) {
        data.value = []
        return
      }
      const { startDate, endDate } = dateRange.value
      const response = await socialMediaClient.chart({
        advertiser_id: advertiserId,
        campaign_id: campaignId,
        date_from: startDate,
        date_to: endDate,
        traffic_source: 'all',
        metric: activeMetric,
        breakdown_by: 'traffic_source',
      })
      data.value = normalizeCampaignChart(response)
    },
  })

  watch([metric, dateRange], () => void fetchChart(), { immediate: true, deep: true })

  return { points: data, isLoading: loading }
}
